import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import Button from '../components/Button';
import { TitleLarge, TitleSmall } from '../components/Typography';
import { colors, inputs, typography } from '../theme/tokens';
import { Recipe, RecipeIngredient, DBIngredient } from '../types/recipe';
import { useToast } from '../contexts/ToastContext';
import { ArrowLeft } from '@phosphor-icons/react';
import noImage from '../assets/no-image.png';
// @ts-ignore
import confetti from 'canvas-confetti';

const PageContainer = styled.div`
  padding: 1rem;
  width: 100%;
  box-sizing: border-box;
`;

const ContentContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const DetailsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  box-sizing: border-box;
`;

const IngredientsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  box-sizing: border-box;
`;

const FormContainer = styled.div`
  padding: 1rem;
  background-color: ${colors.neutral.white};
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  width: 100%;
  box-sizing: border-box;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-family: ${typography.label.medium.fontFamily};
  line-height: ${typography.label.medium.lineHeight};
  color: ${colors.neutral.darkCharcoal};
  font-weight: 500;
`;

const Input = styled.input`
  background: ${inputs.default.background};
  border: ${inputs.default.border};
  color: ${inputs.default.color};
  padding: ${inputs.sizes.default.padding};
  font-size: ${inputs.sizes.default.fontSize};
  border-radius: ${inputs.sizes.default.borderRadius};
  width: 100%;
  font-family: ${typography.paragraph.medium.fontFamily};
  &::placeholder {
    color: ${inputs.default.placeholder};
  }
  &:focus {
    outline: none;
    border-color: ${inputs.default.focusBorder};
  }
  &:disabled {
    background-color: ${inputs.default.disabledBackground};
    color: ${inputs.default.disabledColor};
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  background: ${inputs.default.background};
  border: ${inputs.default.border};
  color: ${inputs.default.color};
  padding: ${inputs.sizes.default.padding};
  font-size: ${inputs.sizes.default.fontSize};
  border-radius: ${inputs.sizes.default.borderRadius};
  width: 100%;
  min-height: 100px;
  font-family: ${typography.paragraph.medium.fontFamily};
  resize: vertical;
  &::placeholder {
    color: ${inputs.default.placeholder};
  }
  &:focus {
    outline: none;
    border-color: ${inputs.default.focusBorder};
  }
`;

const ImagePreviewContainer = styled.div`
  margin-top: 1rem;
  width: 100%;
  max-width: 200px;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  border: ${inputs.default.border};
`;

const ImagePreview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const IngredientsList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${colors.neutral.lightCharcoal};
  border-radius: 6px;
  overflow: hidden;
`;

const IngredientListItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem;
  border-bottom: 1px solid ${colors.neutral.lightCharcoal};
`;

const IngredientName = styled.span`
  flex: 1;
`;

const IngredientQuantity = styled.span`
  min-width: 60px;
  text-align: right;
`;

const IngredientUnit = styled.span`
  min-width: 60px;
  text-align: right;
`;

const InstructionsContainer = styled(FormContainer)`
  margin-top: 2rem;
`;

const InstructionStep = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const StepThumbnail = styled.div`
  width: 150px;
  height: 100px;
  flex-shrink: 0;
  border-radius: 6px;
  overflow: hidden;
  background: ${colors.neutral.lightCharcoal};
  margin-right: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StepThumbnailImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const StepText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  width: 100%;
`;

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  color: ${colors.neutral.darkCharcoal};
  padding: 0.5rem 1rem;
  
  &:hover {
    background-color: ${colors.neutral.offWhite};
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const RecipeDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [ingredients, setIngredients] = useState<RecipeIngredient[]>([]);
  const [dbIngredients, setDbIngredients] = useState<DBIngredient[]>([]);
  const [, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [steps, setSteps] = useState<any[]>([]);
  const [stepsLoading, setStepsLoading] = useState(true);
  const { showToast } = useToast();

  const handlePublishToggle = async () => {
    if (!recipe) return;
    
    try {
      const { error } = await supabase
        .from('Recipes')
        .update({ Publish: !recipe.Publish })
        .eq('RecipeId', recipe.RecipeId);

      if (error) throw error;

      // Fetch the updated recipe data
      const { data: updatedRecipe, error: fetchError } = await supabase
        .from('Recipes')
        .select('*')
        .eq('RecipeId', recipe.RecipeId)
        .single();

      if (fetchError) throw fetchError;

      setRecipe(updatedRecipe);
      showToast(
        updatedRecipe.Publish ? 'Recipe published successfully' : 'Recipe unpublished successfully',
        'success'
      );
      if (updatedRecipe.Publish) {
        confetti({
          particleCount: 200,
          spread: 180,
          origin: { y: 0.6 },
          colors: ['#177508', '#F6B83F', '#CFE531', '#E358C2', '#A0EA95', '#0E4805']
        });
      }
    } catch (error) {
      console.error('Error toggling publish status:', error);
      showToast('Failed to update recipe status', 'error');
    }
  };

  const handleEdit = () => {
    if (!recipe) return;
    navigate(`/recipes/create/${recipe.RecipeId}`);
  };

  useEffect(() => {
    const fetchRecipe = async () => {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('Recipes')
        .select('*')
        .eq('RecipeId', id)
        .single();

      if (error) {
        setError(error.message);
      } else {
        setRecipe(data);
      }
      setIsLoading(false);
    };

    const fetchIngredients = async () => {
      const { data, error } = await supabase
        .from('RecipeIngredients')
        .select('*')
        .eq('RecipeID', id);

      if (!error && data) {
        setIngredients(data);
        
        // Fetch the ingredient details
        const ingredientIds = data.map(ri => ri.IngredientID);
        const { data: dbIngredientsData } = await supabase
          .from('Ingredients')
          .select('*')
          .in('id', ingredientIds);
        
        if (dbIngredientsData) {
          setDbIngredients(dbIngredientsData);
        }
      }
    };

    const fetchSteps = async () => {
      setStepsLoading(true);
      const { data, error } = await supabase
        .from('RecipeSteps')
        .select('*')
        .eq('RecipeID', id)
        .order('Step', { ascending: true });
      if (!error && data) {
        setSteps(data);
      } else {
        setSteps([]);
      }
      setStepsLoading(false);
    };

    if (id) {
      fetchRecipe();
      fetchIngredients();
      fetchSteps();
    }
  }, [id]);

  if (isLoading) return <PageContainer>Loading...</PageContainer>;
  if (!recipe) return <PageContainer>Recipe not found.</PageContainer>;

  return (
    <PageContainer>
      <ButtonContainer>
        <BackButton onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
          Back
        </BackButton>
        <ActionButtons>
          <Button
            variant="outline"
            onClick={handleEdit}
          >
            Edit
          </Button>
          <Button
            variant={recipe.Publish ? "outline" : "filled"}
            color={recipe.Publish ? "danger" : "primary"}
            onClick={handlePublishToggle}
          >
            {recipe.Publish ? 'Unpublish' : 'Publish'}
          </Button>
        </ActionButtons>
      </ButtonContainer>
            <FormGroup>
              <TitleLarge>{recipe.Name}</TitleLarge>
            </FormGroup>
      <ContentContainer>
        <DetailsColumn>
          <FormContainer>
            <FormGroup>
              <TitleSmall>Description</TitleSmall>
              <TextArea value={recipe.Description} disabled />
            </FormGroup>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <FormGroup>
                <Label>Prep Time (minutes)</Label>
                <Input type="number" value={recipe.PrepTime} disabled />
              </FormGroup>
              <FormGroup>
                <Label>Cook Time (minutes)</Label>
                <Input type="number" value={recipe.CookTime} disabled />
              </FormGroup>
            </div>
            <FormGroup>
              <Label>Portion Size</Label>
              <Input type="number" value={recipe.PortionSize} disabled />
            </FormGroup>
            <FormGroup>
              <Label>Category</Label>
              <Input type="text" value={recipe.Category} disabled />
            </FormGroup>
            <FormGroup>
              <Label>Recipe Image</Label>
              {recipe.ImageURL && (
                <ImagePreviewContainer>
                  <ImagePreview src={recipe.ImageURL} alt="Recipe preview" />
                </ImagePreviewContainer>
              )}
            </FormGroup>
            <FormGroup>
              <Label>Free Recipe</Label>
              <Input type="checkbox" checked={recipe.Free} disabled />
            </FormGroup>
          </FormContainer>
        </DetailsColumn>
        <IngredientsColumn>
          <FormContainer>
            <FormGroup>
              <TitleSmall>Ingredients</TitleSmall>
              <IngredientsList>
                {ingredients.length === 0 ? (
                  <IngredientListItem>No ingredients found.</IngredientListItem>
                ) : (
                  ingredients.map((ingredient, idx) => {
                    const dbIngredient = dbIngredients.find(di => di.id === ingredient.IngredientID);
                    return (
                      <IngredientListItem key={idx}>
                        <IngredientName>{dbIngredient?.Name || 'Unknown Ingredient'}</IngredientName>
                        <IngredientQuantity>{ingredient.Quantity}</IngredientQuantity>
                        <IngredientUnit>{ingredient.Unit}</IngredientUnit>
                      </IngredientListItem>
                    );
                  })
                )}
              </IngredientsList>
            </FormGroup>
          </FormContainer>
          <InstructionsContainer>
            <TitleSmall>Instructions</TitleSmall>
            {stepsLoading ? (
              <div>Loading instructions...</div>
            ) : steps.length === 0 ? (
              <div>No instructions available</div>
            ) : (
              steps.map((step, idx) => (
                <InstructionStep key={step.id || idx}>
                  <StepThumbnail>
                    {step.Thumbnail ? (
                      <StepThumbnailImg src={step.Thumbnail} alt={`Step ${step.Step} thumbnail`} />
                    ) : (
                      <StepThumbnailImg src={noImage} alt="No thumbnail available" />
                    )}
                  </StepThumbnail>
                  <StepText>
                    <strong>Step {step.Step}:</strong> {step.Instructions}
                  </StepText>
                </InstructionStep>
              ))
            )}
          </InstructionsContainer>
        </IngredientsColumn>
      </ContentContainer>
    </PageContainer>
  );
};

export default RecipeDetails; 